import type StatamicCollectionRequest from "~/types/requests/StatamicCollectionRequest";
import { ref } from "vue";
import type AlgoliaData from "~/types/AlgoliaData";
import { debounce } from "perfect-debounce";
import { searchClient } from "@algolia/client-search";

export function useArticle() {
    const route = useRoute();
    const router = useRouter();
    const config = useRuntimeConfig();
    const loading = ref(false);
    const page = ref(1);
    const limit = computed(() => {
        // Get limit from query
        if (route.query) {
            for (const [key, value] of Object.entries(route.query)) {
                if (key === "limit") {
                    return value as string;
                }
            }
        }
        return "5";
    });
    const result = ref<AlgoliaData | any>();
    const searchText = ref(route.query.searchArticle ? String(route.query.searchArticle) : "");
    const updateUrl = ref(true);
    // Set page from slug
    const params = route.params as Record<string, unknown>;
    if (params && Object.keys(params).length > 0) {
        for (const [key, value] of Object.entries(params)) {
            if (key.toLowerCase() == "page") {
                page.value = value as number;
            }
        }
    }

    // Initialize Algolia client
    const algoliaClient = searchClient(
        config.public.ALGOLIA_APPLICATION_ID as string,
        config.public.ALGOLIA_API_KEY as string,
    );

    async function performStatamicSearch() {
        const params: Record<string, string> = {};
        // Filter by date_published that is before today
        params["filter[date_published:is_before]"] = new Date().toISOString().substring(0, 10);
        params["fields"] = "id,title,slug,photo,photo_lg,excerpt,date_published";
        params["limit"] = limit.value;
        params["page"] = page.value.toString();
        params["sort"] = "-date_published"; // Get most recent posts

        loading.value = true;
        const searchEndpoint = config.public.STATAMIC_API + "/collections/articles/entries";
        const { data: searchResult } = await useFetch<StatamicCollectionRequest>(searchEndpoint, {
            params: params,
        }).finally(() => {
            loading.value = false;
        });

        result.value = searchResult.value;
    }

    async function performAlgoliaSearch(searchText: string = "") {
        const d = new Date();
        const yesterday = new Date(d);
        yesterday.setDate(d.getDate() - 1);
        const yesterdayUnix = Math.floor(yesterday.getTime() / 1000);

        try {
            const searchResponse = await algoliaClient.search([
                {
                    indexName: `${config.public.ALGOLIA_PREFIX}articles`,
                    params: {
                        query: searchText,
                        filters: `date_published_in_unix <= ${yesterdayUnix}`,
                        hitsPerPage: parseInt(limit.value),
                        page: page.value > 0 ? page.value - 1 : 0, // Index starts from 0
                    },
                },
            ]);

            result.value = searchResponse.results[0];
        } catch (error) {
            console.error("Algolia search error:", error);
            result.value = null;
        }
    }

    async function fetchResult() {
        if (searchText.value === "") {
            await performStatamicSearch();
            return result.value;
        }

        await performAlgoliaSearch(searchText.value);
        return result.value;
    }

    const debouncePerformSearch = debounce(async () => {
        await fetchResult();

        if (updateUrl.value) {
            // Replace page in path
            const path = router.currentRoute.value.path.replace(/\/page\/\d+/, "/page/" + page.value);
            // update the url with the search value
            // this doesn't actually cause the page to reload
            if (searchText.value) {
                router.replace({ path, query: { searchArticle: searchText.value } });
            } else {
                router.replace({ path, query: {} });
            }
        }
    }, 1000);

    watch(searchText, () => {
        page.value = 1; // Reset page to 1
        debouncePerformSearch();
    });
    // Watch router query changes
    watch(
        () => route.query,
        async () => {
            // Set searchText from query to trigger search if searchText is different
            searchText.value = route.query.searchArticle ? String(route.query.searchArticle) : "";
        },
        { immediate: true },
    );

    return {
        loading,
        page,
        searchText,
        updateUrl,
        result,
        fetchResult,
    };
}
